import { browserTimeZone } from './format';

/**
 * Intl.supportedValuesOf (ES2022), which this project's TypeScript lib setting
 * does not declare. Absent in browsers older than Chrome 99, Safari 15.4 and
 * Firefox 93.
 */
const supportedValuesOf = (Intl as unknown as { supportedValuesOf?: (key: 'timeZone') => string[] })
  .supportedValuesOf;

const browserZone = browserTimeZone();

/**
 * Current IANA names that Chromium and Node do not list, because ICU keeps the
 * legacy ID instead (Asia/Calcutta, Europe/Kiev). Without them, a user who
 * searches for "Kolkata" or "Kyiv" finds nothing. The full set: tzdata 2026e
 * zone.tab diffed against Node 24's list, 2026-10-27. Each is an alias Intl
 * accepts, and so does the API.
 */
export const CURRENT_ZONE_NAMES: readonly string[] = [
  'Africa/Asmara',
  'America/Argentina/Buenos_Aires',
  'America/Argentina/Catamarca',
  'America/Argentina/Cordoba',
  'America/Argentina/Jujuy',
  'America/Argentina/Mendoza',
  'America/Atikokan',
  'America/Indiana/Indianapolis',
  'America/Kentucky/Louisville',
  'America/Nuuk',
  'Asia/Ho_Chi_Minh',
  'Asia/Kathmandu',
  'Asia/Kolkata',
  'Asia/Yangon',
  'Atlantic/Faroe',
  'Europe/Kyiv',
  'Pacific/Chuuk',
  'Pacific/Kanton',
  'Pacific/Pohnpei',
];

/**
 * REV-684: the timezones a user may choose. Every IANA zone the browser knows,
 * plus the current names above, plus UTC, which Intl leaves out and which is
 * the account default. revenant-api accepts any valid IANA name
 * (src/middlewares/timezoneField.js), because the lists differ by engine:
 * Chromium has Asia/Calcutta, Firefox and Safari report Asia/Kolkata. Both
 * spellings stay listed, so a zone saved from either engine is matched. A
 * browser without supportedValuesOf offers UTC and its own zone.
 */
export const TIMEZONES: readonly string[] = [
  'UTC',
  ...(supportedValuesOf
    ? [...new Set([...supportedValuesOf('timeZone'), ...CURRENT_ZONE_NAMES])].sort()
    : [browserZone]
  ).filter((zone) => zone !== 'UTC'),
];

/**
 * The browser's own zone, sent at registration as the profile default (Karl's
 * decision, 2026-10-26: no cookie or tracking is involved, so it is not gated
 * on consent). Undefined when the browser reports a zone the list lacks, so
 * the API keeps its UTC default. Named apart from format.ts's
 * browserTimeZone(), which always returns the raw zone.
 */
export function registrationTimezone(): string | undefined {
  return TIMEZONES.includes(browserZone) ? browserZone : undefined;
}
